import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';
import socketService from '../api/socket';

const toStr = (v) => (v != null ? String(v) : '');

// Extract the conversations array from whatever shape the backend returns.
// Handles: [..] | { data:[..] } | { data:{ conversations:[..] } } | { docs:[..] } …
const CONV_KEYS = ['conversations', 'data', 'docs', 'results', 'items'];
const pickArray = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return null;
  for (const k of keys) if (Array.isArray(payload[k])) return payload[k];
  for (const k of keys) {
    const found = payload[k] && pickArray(payload[k], keys);
    if (found) return found;
  }
  return null;
};

// Null-safe sort: missing lastMessageAt goes to the bottom
const byLastMsg = (a, b) => {
  const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
  const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
  return tb - ta;
};

export const useWhatsAppConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const mountedRef = useRef(true);
  const selectedIdRef = useRef(null);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/whatsflow/conversations');
      if (!mountedRef.current) return;

      const raw = pickArray(res.data, CONV_KEYS) || [];

      setConversations([...raw].sort(byLastMsg));
    } catch (err) {
      if (mountedRef.current) {
        setError(err.response?.data?.message || err.message || 'Erreur chargement conversations');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refetch();

    if (!socketService.socket) {
      const token = localStorage.getItem('accessToken');
      socketService.connect(token);
    }

    const socket = socketService.socket;
    if (!socket) return;

    const handleUpdated = (data) => {
      if (!mountedRef.current) return;

      // Accept both {conversation: {...}} and the conversation object directly
      const conv = data?.conversation ?? data;
      if (!conv?._id) return;

      const convId = toStr(conv._id);

      // Distinguish a real new message from a retroactive update (Règle 4 :
      // le backend ré-émet conversation:updated quand le numéro se résout —
      // clientName/clientPhone/hasRealNumber changent mais lastMessageAt non).
      // On ne fait clignoter le badge que si lastMessageAt a réellement avancé.
      let isNewInbound = false;

      setConversations((prev) => {
        const idx = prev.findIndex((c) => toStr(c._id) === convId);
        if (idx >= 0) {
          const prevTs = prev[idx].lastMessageAt ? new Date(prev[idx].lastMessageAt).getTime() : 0;
          const newTs = conv.lastMessageAt ? new Date(conv.lastMessageAt).getTime() : 0;
          isNewInbound = newTs > prevTs;
          return prev.map((c, i) => (i === idx ? { ...c, ...conv } : c)).sort(byLastMsg);
        }
        // Conversation inconnue → premier message entrant
        isNewInbound = true;
        return [conv, ...prev].sort(byLastMsg);
      });

      // Increment unread only for a genuine new message in a conversation
      // that is NOT currently open.
      if (isNewInbound && convId !== toStr(selectedIdRef.current)) {
        setUnreadCounts((prev) => ({
          ...prev,
          [convId]: (prev[convId] || 0) + 1,
        }));
      }
    };

    socket.on('conversation:updated', handleUpdated);

    return () => {
      mountedRef.current = false;
      socket.off('conversation:updated', handleUpdated);
    };
  }, [refetch]);

  const clearUnread = useCallback((conversationId) => {
    const id = toStr(conversationId);
    selectedIdRef.current = id;
    setUnreadCounts((prev) => ({ ...prev, [id]: 0 }));
  }, []);

  return { conversations, loading, error, refetch, unreadCounts, clearUnread };
};

export default useWhatsAppConversations;
